import type { ReactNode } from "react";
import type { Media } from "@zivin-labs/site-content";
import { Figure } from "./Figure";

interface DeanBandPerson {
  name: string;
  role: string;
  photo?: Media | null;
}

interface DeanBandProps {
  /** Heading for the card, e.g. "Office of Admissions". */
  kicker: string;
  /** Retained for callers; the card leads with the kicker as its heading. */
  title?: string;
  /** Descriptive prose about the office. Never a quotation — see the note below. */
  children: ReactNode;
  /** Single office-holder. Ignored when `people` is given. */
  name?: string;
  role?: string;
  photo?: Media | null;
  /** Several office-holders sharing one card, e.g. a Dean plus an Associate Dean. */
  people?: DeanBandPerson[];
  phone?: string;
  email?: string;
}

/**
 * The officer card — portrait, what the office does, and how to reach it.
 *
 * Uses the same shape as the Public Relations card on /disclosures: a contained
 * navy report-card with a portrait column in front and the contact line held in
 * the actions column, rather than the full-bleed band this used to be. That
 * band ran 559px tall for one person's details and pushed everything below it
 * down a screen; this is ~230px and puts the phone number where the eye lands.
 *
 * The prose is always descriptive — what the office does — and never a
 * quotation attributed to the person. The placements page's own header records
 * that a fabricated dean quote was stripped from this codebase once already.
 */
export function DeanBand({
  kicker,
  children,
  name,
  role,
  photo,
  people,
  phone,
  email,
}: DeanBandProps) {
  // A single office-holder is just a one-person roster, so both paths share
  // the same rendering below rather than forking into two card layouts.
  const roster: DeanBandPerson[] = people ?? (name && role ? [{ name, role, photo }] : []);
  const multiple = roster.length > 1;

  return (
    <section className="section section-tight">
      <div className="container">
        <div className={"report-card pr-card dean-card" + (multiple ? " dean-card--group" : "")}>
          <div className={multiple ? "pr-photo-group" : "pr-photo"}>
            {roster.map((p) => (
              <Figure
                key={p.name}
                media={p.photo}
                fallbackLabel={p.name}
                aspect="3/4"
                className="pr-photo-fig"
                sizes={multiple ? "70px" : "(max-width: 700px) 40vw, 160px"}
              />
            ))}
          </div>
          <div className="pr-body">
            <h3>{kicker}</h3>
            <p>{children}</p>
            {multiple ? (
              <div className="pr-person-group">
                {roster.map((p) => (
                  <div key={p.name} className="pr-person">
                    <span className="pr-name">{p.name}</span>
                    <span className="pr-role">{p.role}</span>
                  </div>
                ))}
              </div>
            ) : (
              roster.map((p) => (
                <div key={p.name} className="pr-person">
                  <span className="pr-name">{p.name}</span>
                  <span className="pr-role">{p.role}</span>
                </div>
              ))
            )}
          </div>
          <div className="actions">
            {phone && (
              <>
                <span className="hot">Direct line</span>
                <span className="num">{phone}</span>
              </>
            )}
            {email && (
              <a
                className="btn-link"
                href={`mailto:${email}`}
                style={{ color: "var(--gold-400)", borderBottomColor: "var(--gold-400)" }}
              >
                {email} →
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
